import React from "react";
import { RSD } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../context/I18nContext";
import { Minus, Plus, Trash2, X, ShoppingBag, ChevronLeft } from "lucide-react";
import { useInstallSuppress } from "../../lib/pwaInstall";
import { track } from "../../lib/analytics";

export default function CartSheet({ open, onClose, onCheckout }) {
  const { items, setQty, removeItem, total, orderNote, setOrderNote } = useCart();
  const { t, locale } = useI18n();
  useInstallSuppress(open);
  React.useEffect(() => {
    if (open) track("cart_view", { items: items.length, cart_value: Math.round(total) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;

  const nameOf = (it) => (locale === "en" ? (it.name_en || it.name) : it.name);
  const optsOf = (it) => {
    const src = locale === "en" && it.option_names_en?.length ? it.option_names_en : it.option_names;
    return src || [];
  };

  return (
    <div className="fixed inset-0 z-50 max-w-md mx-auto" data-testid="cart-sheet">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] flex flex-col animate-slide-up">
        <div className="flex items-center gap-2 p-5 border-b">
          <button onClick={onClose} data-testid="cart-back" aria-label={t("cart.back", "Nazad na meni")}
            className="bg-slate-100 rounded-full p-2 active:scale-95 transition-transform"><ChevronLeft size={18} /></button>
          <h2 className="font-display font-black text-xl flex-1">{t("cart.title", "Vaša korpa")}</h2>
          <button onClick={onClose} data-testid="close-cart" className="bg-slate-100 rounded-full p-2"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {items.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingBag size={40} className="mx-auto mb-2 opacity-40" />
              {t("cart.empty", "Korpa je prazna")}
            </div>
          )}
          {items.map((it) => (
            <div key={it.uid} className="flex gap-3 bg-slate-50 rounded-2xl p-3" data-testid={`cart-item-${it.product_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold">{nameOf(it)}</p>
                {optsOf(it).length > 0 && <p className="text-xs text-slate-500 mt-0.5">{optsOf(it).join(", ")}</p>}
                {it.person && <p className="text-xs mt-0.5"><span className="font-bold text-brand-red">{t("item.for_prefix", "Za")}: </span><span className="font-semibold text-slate-700">{it.person}</span></p>}
                {it.note && <p className="text-xs text-slate-500 mt-0.5"><span className="font-semibold">{t("item.note_prefix", "Napomena")}: </span><span className="italic">{it.note}</span></p>}
                <p className="font-display font-black text-brand-red mt-1">{RSD(it.unit_computed * it.quantity)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.uid)} className="text-slate-400"><Trash2 size={16} /></button>
                <div className="flex items-center gap-2 bg-white rounded-full px-1.5 py-1">
                  <button onClick={() => setQty(it.uid, it.quantity - 1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><Minus size={13} /></button>
                  <span className="font-bold w-4 text-center text-sm">{it.quantity}</span>
                  <button onClick={() => setQty(it.uid, it.quantity + 1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><Plus size={13} /></button>
                </div>
              </div>
            </div>
          ))}
          {items.length > 0 && (
            <div data-testid="order-note-field" className="pt-1">
              <label className="text-sm font-bold flex items-center justify-between">
                <span>{t("cart.note_label", "Napomena za restoran")}</span>
                <span className="text-xs font-normal text-slate-400">{t("cart.note_optional", "Opciono")}</span>
              </label>
              <textarea data-testid="order-note-input" value={orderNote} maxLength={250} rows={2}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder={t("cart.note_placeholder", "Npr. Spakujte u dve odvojene kese.")}
                className="mt-1 w-full rounded-2xl bg-slate-100 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-red resize-none" />
              <div className="text-right text-[11px] text-slate-400 mt-0.5">{orderNote.length} / 250</div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="p-5 border-t">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-500">{t("cart.total", "Ukupno")}</span>
              <span className="font-display font-black text-xl">{RSD(total)}</span>
            </div>
            <button data-testid="checkout-btn" onClick={onCheckout} className="w-full bg-brand-red text-white rounded-full py-4 font-display font-bold active:scale-[0.98] transition-transform">
              {t("cart.checkout", "Nastavi na naručivanje")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
